// Спонсоры лиг: разбор ответа /partners/adminPartners и подготовка запросов.
// Файл одинаковый в сайте (ingame-site-admin/src/utilities/partners.js) и в
// приложении (ingame-admin/src/utilities/partners.js) — при изменении править оба.
// Контракт рутов — ingame-site-admin/PARTNERS_BACKEND.md.

export const MAX_PARTNER_LOGO_BYTES = 1024 * 1024;
export const PARTNER_NAME_MAX = 100;
export const PARTNER_DESCRIPTION_MAX = 100;
export const PARTNER_HREF_MAX = 1000;

const pickList = (data, key) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[key])) return data[key];
  if (data && Array.isArray(data.data)) return data.data;
  return [];
};

export const normalizePartner = (raw) => ({
  id: Number(raw.id !== undefined ? raw.id : raw.partner_id),
  client_id: raw.client_id !== undefined && raw.client_id !== null ? Number(raw.client_id) : null,
  name: raw.name || '',
  description: raw.description || '',
  image: raw.image || '',
  href: raw.href || '',
  leagues: pickList(raw.leagues, 'leagues')
    .map(link => ({
      league_id: Number(link.league_id),
      active: Number(link.active) !== 0,
      priority: Number(link.priority) || 1,
    }))
    .filter(link => !isNaN(link.league_id)),
});

// Спонсоры по алфавиту — так их ищут глазами в списке
export const normalizePartners = (data) => pickList(data, 'partners')
  .map(normalizePartner)
  .filter(partner => !isNaN(partner.id))
  .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

// Спонсоры одной лиги в порядке показа: [{partner_id, active}]
export const leaguePartnersFrom = (partners, leagueId) => partners
  .map(partner => {
    const link = partner.leagues.find(item => item.league_id === Number(leagueId));
    return link ? {partner_id: partner.id, active: link.active, priority: link.priority} : null;
  })
  .filter(Boolean)
  .sort((a, b) => (a.priority - b.priority) || (a.partner_id - b.partner_id))
  .map(({partner_id, active}) => ({partner_id, active}));

// Тело для /leagues/setLeaguePartners: порядок в списке и есть priority
export const leaguePartnersPayload = (leagueId, draft) => ({
  league_id: Number(leagueId),
  partners: draft.map((item, index) => ({
    partner_id: item.partner_id,
    active: item.active ? 1 : 0,
    priority: index + 1,
  })),
});

export const sameLeaguePartners = (a, b) => a.length === b.length
  && a.every((item, index) => item.partner_id === b[index].partner_id
    && Boolean(item.active) === Boolean(b[index].active));

export const moveItem = (list, index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
};

export const sameIdSet = (a, b) => {
  if (a.length !== b.length) return false;
  const set = {};
  a.forEach(id => { set[id] = true; });
  return b.every(id => set[id]);
};

// Ссылка без схемы — считаем https
export const normalizeHref = (value) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const HREF_RE = /^https?:\/\/[^\s/?#]+\.[^\s/?#]+([/?#]\S*)?$/i;

// Ошибки формы спонсора: {name, description, href, logo}; пустой объект — всё верно
export const validatePartnerForm = (form, hasLogo) => {
  const errors = {};
  const name = (form.name || '').trim();
  if (!name) errors.name = 'Введите название спонсора.';
  else if (name.length > PARTNER_NAME_MAX) errors.name = `Не длиннее ${PARTNER_NAME_MAX} символов.`;
  if ((form.description || '').trim().length > PARTNER_DESCRIPTION_MAX) {
    errors.description = `Не длиннее ${PARTNER_DESCRIPTION_MAX} символов.`;
  }
  const href = normalizeHref(form.href);
  if (href && (!HREF_RE.test(href) || href.length > PARTNER_HREF_MAX)) {
    errors.href = 'Проверьте ссылку — например, https://example.ru';
  }
  if (!hasLogo) errors.logo = 'Добавьте логотип спонсора.';
  return errors;
};

// Чемпионаты с лигами для выбора «где показывать»; пустые чемпионаты не нужны
export const leagueGroupsFrom = (structure) => {
  if (!structure) return [];
  const groups = structure.championats
    .map(ch => ({id: ch.id, name: ch.name, leagues: structure.tree[ch.id] || []}))
    .filter(group => group.leagues.length > 0);
  if (structure.orphanLeagues && structure.orphanLeagues.length) {
    groups.push({id: 'none', name: 'Без чемпионата', leagues: structure.orphanLeagues});
  }
  return groups;
};

export const plural = (n, one, few, many) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};
